/**
 * §2.6 — deactivation is a **flow, not a toggle**, and this screen is the flow.
 *
 * The four steps are the four the spec lists, in order, each one visible:
 *
 *   1. Collect everything blocking — in Phase 0, nodes owned by this person.
 *   2. Require reassignment before proceeding.
 *   3. Set status, stamp `deactivated_at`, revoke every session, revoke every
 *      person-specific grant.
 *   4. Never delete the record.
 *
 * Two decisions in the rendering are worth naming. The blockers are shown
 * before anything can be confirmed, because "what is stopping this" is the
 * question the flow exists to answer — a screen that only reports failure after
 * a submit makes the admin discover the list one refusal at a time. And step 3
 * is previewed with real counts from the API rather than described in the
 * abstract: "revokes 2 sessions and 5 grants" is a sentence someone can decide
 * about.
 *
 * The API refuses a still-blocked deactivation regardless of what this screen
 * allows. This renders the flow; it does not enforce it.
 */

import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  type DeactivationInput,
  useDeactivate,
  useDeactivationPreview,
  usePeoplePicker,
} from '../../../../api/queries.ts';
import type { DeactivationResult } from '../../../../api/types.ts';
import { Badge } from '../../../../components/ui/badge.tsx';
import { Button } from '../../../../components/ui/button.tsx';
import { cn } from '../../../../components/ui/cn.ts';
import { Alert, ErrorState, Loading } from '../../../../components/ui/feedback.tsx';
import { Select, TextField } from '../../../../components/ui/field.tsx';
import { Card, Mono, PageHeader } from '../../../../components/ui/layout.tsx';

type StepState = 'done' | 'current' | 'blocked' | 'upcoming';

function Step({
  index,
  title,
  state,
  description,
  children,
}: {
  index: number;
  title: string;
  state: StepState;
  description: string;
  children?: React.ReactNode;
}) {
  const badge =
    state === 'done' ? (
      <Badge tone="success">done</Badge>
    ) : state === 'blocked' ? (
      <Badge tone="error">blocking</Badge>
    ) : state === 'current' ? (
      <Badge tone="brand">now</Badge>
    ) : (
      <Badge tone="gray">waiting</Badge>
    );

  return (
    <Card
      padded={false}
      className={cn(state === 'upcoming' && 'opacity-60', state === 'blocked' && 'border-error')}
    >
      <div className="flex items-start gap-3 border-b border-secondary px-5 py-4">
        <span
          aria-hidden="true"
          className={cn(
            'grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold',
            state === 'done'
              ? 'bg-success-secondary text-success-primary'
              : state === 'blocked'
                ? 'bg-error-secondary text-error-primary'
                : state === 'current'
                  ? 'bg-brand-solid text-brand-on-solid'
                  : 'bg-tertiary text-tertiary',
          )}
        >
          {index}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-md font-semibold text-primary">{title}</h2>
            {badge}
          </div>
          <p className="mt-0.5 text-sm text-tertiary">{description}</p>
        </div>
      </div>
      {children ? <div className="px-5 py-4">{children}</div> : null}
    </Card>
  );
}

export function DeactivateUserPage() {
  const { personId } = useParams({
    from: '/_authed/_employee/settings/workspace/users/$personId/deactivate',
  });
  const navigate = useNavigate();

  const preview = useDeactivationPreview(personId);
  const picker = usePeoplePicker();
  const deactivate = useDeactivate(personId);

  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [reassignAllTo, setReassignAllTo] = useState<string | undefined>();
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<DeactivationResult | null>(null);

  const ownedNodes = preview.data?.blockers.ownedNodes ?? [];

  /**
   * Everything must have a destination before step 3 opens. `reassignAllTo` is
   * the API's own fallback for anything left unassigned, so it counts as cover
   * for every remaining node rather than being a separate mode.
   */
  const unresolved = useMemo(
    () => (reassignAllTo ? [] : ownedNodes.filter((node) => !assignments[node.id])),
    [ownedNodes, assignments, reassignAllTo],
  );

  const pickerOptions = useMemo(
    () =>
      (picker.data?.people ?? [])
        .filter((candidate) => candidate.id !== personId)
        .map((candidate) => ({
          value: candidate.id,
          label: candidate.name,
          description: candidate.email,
        })),
    [picker.data, personId],
  );

  if (preview.isPending) return <Loading label="Collecting what is blocking…" />;
  if (preview.isError) return <ErrorState error={preview.error} />;

  const { person, blocked, willRevoke, blockers } = preview.data;

  if (result) {
    return (
      <>
        <PageHeader title={`${person.name} is deactivated`} />
        <Alert tone="success" title="The flow completed">
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-5">
            <li>{result.reassignedNodes} node(s) reassigned before anything else happened.</li>
            <li>{result.revokedSessions} session(s) revoked — immediately, not at expiry.</li>
            <li>{result.revokedGrants} person-specific grant(s) deleted.</li>
            <li>
              The person record is intact. Their role no longer reaches anything either: the
              resolver requires <span className="font-mono">status = 'active'</span> before it
              considers a single grant.
            </li>
          </ul>
        </Alert>
        <div className="flex gap-3">
          <Link to="/people/$personId" params={{ personId }} className="no-underline">
            <Button variant="secondary">Back to their record</Button>
          </Link>
          <Link to="/schema/access/people/$personId" params={{ personId }} className="no-underline">
            <Button variant="primary">Check what they can reach now</Button>
          </Link>
        </div>
      </>
    );
  }

  if (person.account === null) {
    return (
      <>
        <PageHeader title={person.name} />
        <Alert tone="info" title="There is no access to withdraw">
          This person has no account, so there is no status to change, no session to revoke, and no
          baseline to deny. Deactivation acts on a login, and they have never had one.{' '}
          <Link to="/people/$personId" params={{ personId }}>
            Back to their record
          </Link>
          .
        </Alert>
      </>
    );
  }

  if (person.account.status === 'deactivated') {
    return (
      <>
        <PageHeader title={person.name} />
        <Alert tone="info" title="Already deactivated">
          There is nothing left to run.{' '}
          <Link to="/people/$personId" params={{ personId }}>
            Back to their record
          </Link>
          .
        </Alert>
      </>
    );
  }

  const step1: StepState = blocked ? 'blocked' : 'done';
  const step2: StepState = !blocked ? 'done' : unresolved.length === 0 ? 'done' : 'current';
  const step3: StepState = step2 === 'done' ? 'current' : 'upcoming';

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/people/$personId" params={{ personId }} className="text-tertiary no-underline">
            {person.name}
          </Link>
        }
        title="Deactivate"
        description="Offboarding is a flow with an order to it. Nothing below happens until everything above it is settled, and the record is never deleted."
      />

      <Step
        index={1}
        title="What is blocking"
        state={step1}
        description="In Phase 0 this is nodes owned by this person. Phase 1b adds in-flight approvals where they are a required reviewer."
      >
        {blocked ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-secondary">
              {blockers.ownedNodes.length} node(s) are owned by {person.name}. Ownership is required
              and non-null, so each one needs a new owner before deactivation can proceed.
            </p>
            <ul className="flex flex-col divide-y divide-secondary rounded-lg border border-secondary">
              {blockers.ownedNodes.map((node) => (
                <li key={node.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <Link
                    to="/nodes/$nodeId"
                    params={{ nodeId: node.id }}
                    className="font-medium text-primary no-underline hover:underline"
                  >
                    {node.title}
                  </Link>
                  <Badge tone="outline" mono>
                    {node.type}
                  </Badge>
                  <Mono className="ml-auto">{node.id}</Mono>
                </li>
              ))}
            </ul>
            {blockers.pendingApprovals.length > 0 ? (
              <p className="text-sm text-warning-primary">
                {blockers.pendingApprovals.length} pending approval(s) also block.
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-secondary">
            Nothing is blocking. {person.name} owns no live nodes.
          </p>
        )}
      </Step>

      <Step
        index={2}
        title="Reassign"
        state={step2}
        description="Ownership has to land somewhere before it can be taken away. Only active people can receive it."
      >
        {!blocked ? (
          <p className="text-sm text-tertiary">Nothing to reassign.</p>
        ) : picker.isPending ? (
          <Loading label="Loading candidates…" />
        ) : (
          <div className="flex flex-col gap-4">
            <Select
              label="Hand everything to one person"
              className="max-w-sm"
              placeholder="Choose a person…"
              options={pickerOptions}
              selectedKey={reassignAllTo ?? null}
              onSelectionChange={(value) => {
                setReassignAllTo(value);
                setAssignments({});
              }}
              description="The common case. Anything you set individually below wins over this."
            />

            <details className="rounded-lg border border-secondary">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-secondary">
                Assign node by node instead
              </summary>
              <ul className="flex flex-col divide-y divide-secondary border-t border-secondary">
                {ownedNodes.map((node) => (
                  <li
                    key={node.id}
                    className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-end"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-primary">{node.title}</p>
                      <Mono>{node.id}</Mono>
                    </div>
                    <Select
                      label="New owner"
                      className="sm:w-64"
                      placeholder={reassignAllTo ? 'Falls to the person above' : 'Choose…'}
                      options={pickerOptions}
                      selectedKey={assignments[node.id] ?? null}
                      onSelectionChange={(value) =>
                        setAssignments((current) => ({ ...current, [node.id]: value }))
                      }
                    />
                  </li>
                ))}
              </ul>
            </details>

            {unresolved.length > 0 ? (
              <Alert tone="warning">
                {unresolved.length} node(s) still have nowhere to go. Pick someone to hand
                everything to, or assign each one individually.
              </Alert>
            ) : null}
          </div>
        )}
      </Step>

      <Step
        index={3}
        title="Revoke and record"
        state={step3}
        description="Status, timestamp, every session, every person-specific grant, and any live magic link — in one transaction."
      >
        <div className="flex flex-col gap-4">
          <ul className="flex flex-col gap-1 text-sm text-secondary">
            <li>
              <strong className="text-primary">{willRevoke.sessions}</strong> active session(s) will
              be revoked. Database-backed sessions is exactly why this takes effect now rather than
              whenever a token would have expired.
            </li>
            <li>
              <strong className="text-primary">{willRevoke.personGrants}</strong> person-specific
              grant(s) will be deleted. Role-based grants cannot be revoked this way — the resolver
              denies a non-active person before it reads any of them.
            </li>
            <li>The person record itself is kept, so authorship and audit references survive.</li>
          </ul>

          <TextField
            label="Reason"
            className="max-w-lg"
            value={reason}
            onChange={setReason}
            multiline
            rows={2}
            description="Recorded on the event. Optional here, but this is the line someone reads months later."
          />

          {deactivate.isError ? <ErrorState error={deactivate.error} /> : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="destructive"
              isDisabled={step3 !== 'current'}
              isPending={deactivate.isPending}
              onPress={() => {
                const explicit = Object.entries(assignments)
                  .filter(([, ownerId]) => Boolean(ownerId))
                  .map(([nodeId, ownerId]) => ({ nodeId, ownerId }));
                const input: DeactivationInput = {
                  ...(explicit.length > 0 ? { reassignments: explicit } : {}),
                  ...(reassignAllTo ? { reassignAllTo } : {}),
                  ...(reason.trim() ? { reason: reason.trim() } : {}),
                };
                deactivate.mutate(input, { onSuccess: setResult });
              }}
            >
              Deactivate {person.name}
            </Button>
            <Button
              variant="tertiary"
              onPress={() => void navigate({ to: '/people/$personId', params: { personId } })}
            >
              Cancel
            </Button>
            {step3 !== 'current' ? (
              <span className="text-sm text-tertiary">Settle step 2 first.</span>
            ) : null}
          </div>
        </div>
      </Step>
    </>
  );
}
